/**
 * Plan limits + assertion helpers.
 *
 * Source of truth: `plans/03-architecture/billing.md`.
 *
 *   discover  — 1 account · 1 active campaign  · 500   AI calls/mo
 *   pro       — 1 account · 5 active campaigns · 1500  AI calls/mo
 *   scale     — 2 accounts · 10 active campaigns · 4000 AI calls/mo
 *   agency    — 3 accounts · ∞ active campaigns · ∞    AI calls/mo
 *
 * Per-plan AI micro-USD budgets are mirrored here for convenience (used by
 * the AI Worker's middleware).
 */
import { AppError } from './errors';
export const PLAN_LIMITS = {
    discover: {
        accounts: 1,
        activeCampaigns: 1,
        aiCallsPerMonth: 500,
        aiBudgetCentsPerMonth: 500, // $5
    },
    pro: {
        accounts: 1,
        activeCampaigns: 5,
        aiCallsPerMonth: 1500,
        aiBudgetCentsPerMonth: 1500, // $15
    },
    scale: {
        accounts: 2,
        activeCampaigns: 10,
        aiCallsPerMonth: 4000,
        aiBudgetCentsPerMonth: 3000, // $30
    },
    agency: {
        accounts: 3,
        activeCampaigns: Infinity,
        aiCallsPerMonth: Infinity,
        aiBudgetCentsPerMonth: Infinity,
    },
};
/** True when adding one more would exceed the plan's account quota. */
export function isAtAccountQuota(plan, currentCount) {
    return currentCount >= PLAN_LIMITS[plan].accounts;
}
/** True when launching one more would exceed the plan's active-campaign quota. */
export function isAtCampaignQuota(plan, currentActiveCount) {
    return currentActiveCount >= PLAN_LIMITS[plan].activeCampaigns;
}
/** True when an additional AI call would exceed the plan's monthly call cap. */
export function isAtAiQuota(plan, monthlyCalls) {
    return monthlyCalls >= PLAN_LIMITS[plan].aiCallsPerMonth;
}
/** True when an additional AI call would exceed the plan's micro-USD budget. */
export function isAtAiBudget(plan, monthlyBudgetCents) {
    return monthlyBudgetCents >= PLAN_LIMITS[plan].aiBudgetCentsPerMonth;
}
// ────────────────────────────────────────────────────────────────────────────
// Assertion helpers — throw AppError so callers (Hono middleware, server
// actions) can surface the canonical `plan.*` error codes.
// ────────────────────────────────────────────────────────────────────────────
export function assertWithinAccountQuota(plan, currentCount) {
    if (isAtAccountQuota(plan, currentCount)) {
        throw new AppError('plan.account_quota', {
            httpStatus: 403,
            i18nParams: {
                plan,
                limit: PLAN_LIMITS[plan].accounts,
                current: currentCount,
            },
        });
    }
}
export function assertWithinCampaignQuota(plan, currentActiveCount) {
    if (isAtCampaignQuota(plan, currentActiveCount)) {
        throw new AppError('plan.campaign_quota', {
            httpStatus: 403,
            i18nParams: {
                plan,
                limit: PLAN_LIMITS[plan].activeCampaigns,
                current: currentActiveCount,
            },
        });
    }
}
export function assertWithinAiQuota(plan, monthlyCalls) {
    if (isAtAiQuota(plan, monthlyCalls)) {
        throw new AppError('plan.ai_quota', {
            httpStatus: 429,
            retryable: true,
            i18nParams: {
                plan,
                limit: PLAN_LIMITS[plan].aiCallsPerMonth,
                current: monthlyCalls,
            },
        });
    }
}
export function assertWithinAiBudget(plan, monthlyBudgetCents) {
    if (isAtAiBudget(plan, monthlyBudgetCents)) {
        throw new AppError('plan.ai_budget', {
            httpStatus: 429,
            retryable: true,
            i18nParams: {
                plan,
                limitCents: PLAN_LIMITS[plan].aiBudgetCentsPerMonth,
                currentCents: monthlyBudgetCents,
            },
        });
    }
}
//# sourceMappingURL=plan.js.map